// OfferAccordionList.tsx
import React from "react";
import { Accordion, Loader } from "rsuite";
import { useTypedSelector } from "@/redux/hooks";
import MQ from "@/utils/MatchQueryUtils";
import i18n from "@/i18n";
import DataBusDefaults from "@/services/DataBusDefaults";
import ListComponent from "@/configurable/components/ListComponent/ListComponent";
import { AssetTypes } from "@/model/AssetTypes";
import { APActivity } from "../../../../../ActivityInterfaces";
import APOfferEntry from "../APOfferEntry";
import { useActivityConstants } from "../../../../../ActivityHooks";
import { APOffer } from "../../APOffer.Interface";
import { useActivityStruct } from "@/apps/tatar/activityApp/ActivityStructContext";
import VacancyOfferUtils from "@/apps/tatar/activityApp/specific/vacancy/vacancy-applicant/utils/VacancyOfferUtils";

export interface OfferAccordionListProps {
  activity: APActivity;
  statuses?: string[];
  identifierPrefix: string;
  /** The base URL, for example "/offers", from the parent route */
  baseUrl: string;
  /** The currently active status */
  activeStatus?: string;
  renderEntry?: (offer: APOffer, activity: APActivity) => JSX.Element;
  renderHeader?: (
    status: string,
    tableData: any,
    defaultHeader: React.ReactNode
  ) => React.ReactNode;
  additionalQueryBuilder?: (activityId: string, status: string) => any;
  additionalStatus?: { [key: string]: string[] };
}

export const APOfferAccordionList = (props: OfferAccordionListProps) => {
  const {
    activity,
    statuses = ["accepted", "new", "declined", "archived"],
    identifierPrefix,
    baseUrl,
    activeStatus = statuses[0],
    renderEntry,
    renderHeader,
    additionalQueryBuilder,
    additionalStatus,
  } = props;

  return (
    <Accordion
      activeKey={activeStatus}
      bordered
      onSelect={(eventKey) => {
        DataBusDefaults.route({
          route: `${baseUrl}/${eventKey}`,
        });
      }}
    >
      {statuses.map((status) => {
        const additionalStatusList =
          additionalStatus && additionalStatus[status]
            ? additionalStatus[status]
            : undefined;

        return (
          <APOfferAccordionPanel
            key={status}
            activity={activity}
            status={status}
            identifierPrefix={identifierPrefix}
            renderEntry={renderEntry}
            renderHeader={renderHeader}
            additionalQueryBuilder={additionalQueryBuilder}
            additionalStatus={additionalStatusList}
          />
        );
      })}
    </Accordion>
  );
};

interface OfferAccordionPanelProps {
  activity: APActivity;
  status: string;
  identifierPrefix: string;
  renderEntry?: (item: any, activity: APActivity) => React.ReactNode;
  renderHeader?: (
    status: string,
    tableData: any,
    defaultHeader: React.ReactNode
  ) => React.ReactNode;
  additionalQueryBuilder?: (activityId: string, status: string) => any;
  additionalStatus?: string[];
}

const APOfferAccordionPanel = (props: OfferAccordionPanelProps) => {
  const {
    activity,
    status,
    identifierPrefix,
    renderEntry,
    renderHeader,
    additionalQueryBuilder,
    additionalStatus = [],
  } = props;
  const activityStruct = useActivityStruct();

  const isActionFlow = VacancyOfferUtils.isVacancyActionFlowApproval(
    activity,
    activityStruct
  );

  const constants = useActivityConstants();
  const identifier = `${identifierPrefix}-${status}`;
  const tableData = useTypedSelector(
    (state) => state.application.infiniteTables[identifier]
  );

  const defaultHeader = (
    <div className="section-title">
      <span>{i18n.t(`apTemplate:OfferList.Status.${status}`, status)}</span>
      {tableData?.loading ? (
        <Loader size="xs" />
      ) : (
        <span className="count">({tableData?.totalCount})</span>
      )}
    </div>
  );

  const header = renderHeader
    ? renderHeader(status, tableData, defaultHeader)
    : defaultHeader;

  const additionalStatusMatchQuery =
    additionalStatus.length > 0
      ? MQ.or(MQ.in("data.status", additionalStatus))
      : null;

  return (
    <Accordion.Panel header={header} eventKey={status}>
      <ListComponent
        assetType={AssetTypes.ActivityRelated.Offer}
        identifier={identifier}
        cleanupOnUnmount
        asPost
        additionalMatchQuery={
          additionalQueryBuilder
            ? additionalQueryBuilder(activity._id, status)
            : MQ.and(
                MQ.eq("data.linkedActivity.assetId", activity._id),
                MQ.or(MQ.eq("data.status", status), additionalStatusMatchQuery),
                isActionFlow ? MQ.isNotNull("data.meta.applicationId") : null
              )
        }
        render={(offer: APOffer) => (
          <div key={offer._id} className="offer-entry">
            {renderEntry ? (
              renderEntry(offer, activity)
            ) : (
              <APOfferEntry editable offer={offer} constants={constants} />
            )}
          </div>
        )}
      />
    </Accordion.Panel>
  );
};
